import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';

import { useDb, useEnsureAuth } from '../firebase/FirebaseContext';
import { hasV1PreviewOverride, useV1PreviewReleased } from '../lib/v1PreviewFlag';

/**
 * The ONE resolver for the board gift-guide rail (SPEC §2 "Rollout"). Every
 * surface the feature owns — the rail itself and the "← <Recipient>'s board"
 * link on all 74 guide pages — gates on this single resolved value, so the two
 * halves can never disagree about whether the feature is on.
 *
 *   boardGuideRailEnabled = ?boardGuideRail=1          -> true   (query override)
 *                           : featureFlags/boardGuideRail.enabled ?? false
 *
 * TWO MECHANISMS, DELIBERATELY DIFFERENT:
 *
 *  - `?boardGuideRail=1` is the INTERVIEW COHORT door. Hand a tester a
 *    givethea.com link carrying the param and they get the full experience
 *    while the general-release switch stays off. It takes precedence, and
 *    `enabled: false` does NOT revoke it — this is a release switch, not a
 *    universal kill switch (SPEC §2, §9.1). Because the param is the cohort's
 *    only carrier, it has to survive navigation: `withBoardGuideRailParam`
 *    below is the shared helper every such hop uses (card hrefs, the back
 *    link's boardPath, and all three regenerate navs in
 *    RecommendationResultsPage) so a tester keeps the rail across a
 *    regenerate, not just a guide round-trip.
 *
 *  - `featureFlags/boardGuideRail.enabled` is the GENERAL-RELEASE switch.
 *    Default off; flipping it true launches to everyone.
 *
 * FAIL-CLOSED, AND TODAY IT ALWAYS FAILS. The release switch is read from
 * Firestore and any absence/error resolves to `false`. That is not merely
 * defensive: as of this commit `featureFlags` is NOT in thea-serverless's
 * client read allowlist (firestore.rules' catch-all allows reads only for
 * `product` and `materializedSimilarProducts`, and there is no `featureFlags`
 * block), so this read is permission-denied for every client and the switch
 * resolves false for everyone. The cohort override is therefore the only live
 * door until a rules block lands. This is written as a real read rather than a
 * hardcoded `false` precisely so that flipping it on is a rules + flag-doc
 * change with no frontend deploy — see the PR body's "Backend prerequisite".
 *
 * NO `isV4Preview()` ANYWHERE in this feature (SPEC §2): that gate is
 * unconditionally true now, so pairing it with a real flag would read as a
 * second condition while contributing nothing.
 *
 * THE-955 COMPOSITION: `?v1Preview=1` and `featureFlags/v1Preview.enabled`
 * are the V1 combined-QA master switch (`../lib/v1PreviewFlag`) — they force
 * EVERY V1 piece on at once for the one combined pass, this rail included.
 * Both the override check and the release read below OR in the master
 * switch's equivalent rather than being checked independently, so this file
 * can't end up disagreeing with the master switch about whether the rail is
 * live.
 */

/** Query param carrying the interview-cohort override. */
export const BOARD_GUIDE_RAIL_PARAM = 'boardGuideRail';

/** The only value that activates the override. Any other value is ignored. */
export const BOARD_GUIDE_RAIL_PARAM_ON = '1';

/** Firestore location of the general-release switch (server-written). */
export const FEATURE_FLAGS_COLLECTION = 'featureFlags';
export const BOARD_GUIDE_RAIL_FLAG_DOC = 'boardGuideRail';

/** Shape of `featureFlags/boardGuideRail`. */
export interface BoardGuideRailFlagDoc {
  enabled?: boolean;
}

/**
 * Is the cohort override present in this location's query string? Pure, so the
 * URL helper and the hook share one definition of "the override is on" and a
 * test can drive it without a Router.
 */
export function hasBoardGuideRailOverride(search: string): boolean {
  // The V1 master switch first, and before the empty-search exit: it can be
  // on with a bare URL, remembered from an earlier tester link
  // (lib/previewMemory.ts).
  if (hasV1PreviewOverride(search)) return true;
  if (!search) return false;
  try {
    return new URLSearchParams(search).get(BOARD_GUIDE_RAIL_PARAM) === BOARD_GUIDE_RAIL_PARAM_ON;
  } catch {
    // A malformed query string is not a reason to crash a board render.
    return false;
  }
}

/**
 * Append `?boardGuideRail=1` to an in-app path when the override is active —
 * the shared helper for EVERY hop that has to carry the cohort forward.
 *
 * Deliberately string surgery on a relative path rather than `new URL()`:
 * these are router paths ('/quiz/results/a/b', '/guide/little-artist'), not
 * absolute URLs, and `new URL()` needs a base. Preserves any existing query
 * and hash, and is idempotent — carrying the param twice through a
 * card -> guide -> back-link round trip must not produce `?x=1&x=1`.
 */
export function withBoardGuideRailParam(path: string, active: boolean): string {
  if (!active || !path) return path;
  const hashAt = path.indexOf('#');
  const hash = hashAt === -1 ? '' : path.slice(hashAt);
  const beforeHash = hashAt === -1 ? path : path.slice(0, hashAt);
  const queryAt = beforeHash.indexOf('?');
  const pathname = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const search = queryAt === -1 ? '' : beforeHash.slice(queryAt + 1);
  const params = new URLSearchParams(search);
  // set(), not append() — idempotence.
  params.set(BOARD_GUIDE_RAIL_PARAM, BOARD_GUIDE_RAIL_PARAM_ON);
  return `${pathname}?${params.toString()}${hash}`;
}

export interface UseBoardGuideRailEnabledOptions {
  /**
   * Skip the release-switch read entirely. Callers that already know the
   * feature is irrelevant to this render pass it — notably `useGuideBackLink`,
   * which sees no board origin in `location.state` on a direct /guide/* visit
   * and so must not spend a Firestore read on every organic guide landing.
   * A parameter rather than an `if` because hooks can't be conditional (the
   * same opt-out shape `useGuidePreviewDoc(skip)` uses).
   */
  skip?: boolean;
}

/**
 * The general-release switch alone. Split out from the resolver below so tests
 * can exercise the Firestore half without a query string, and so the resolver
 * reads as the spec's one-line expression.
 */
function useBoardGuideRailRelease(skip: boolean): boolean {
  const db = useDb();
  const ensureAuth = useEnsureAuth();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (skip) return undefined;
    let cancelled = false;

    (async () => {
      try {
        await ensureAuth();
        const snap = await getDoc(
          doc(db, FEATURE_FLAGS_COLLECTION, BOARD_GUIDE_RAIL_FLAG_DOC),
        );
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as BoardGuideRailFlagDoc;
        // `=== true` not truthiness: a stray string in the flag doc must not
        // launch a feature to everyone.
        if (data?.enabled === true) setEnabled(true);
      } catch {
        // Denied (today's rules), offline, or absent — all mean "not released".
        // Deliberately silent: this read is EXPECTED to fail in production
        // until the rules block lands, and a console warning on every board
        // render would be noise that trains people to ignore the console.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, ensureAuth, skip]);

  return enabled;
}

/**
 * The resolved gate. Reactive on the URL: the override is read from
 * `useLocation()` on every render, so a client-side nav that drops or adds the
 * param flips the feature without a remount.
 */
export function useBoardGuideRailEnabled(
  options: UseBoardGuideRailEnabledOptions = {},
): boolean {
  const { search } = useLocation();
  // Already includes the v1Preview override — see `hasBoardGuideRailOverride`.
  const override = hasBoardGuideRailOverride(search);
  // When the override already decided `true`, both release reads can only
  // agree — skip them rather than spend (currently denied) Firestore round
  // trips.
  const skipReleaseReads = options.skip === true || override;
  const released = useBoardGuideRailRelease(skipReleaseReads);
  const v1PreviewReleased = useV1PreviewReleased(skipReleaseReads);
  return override || released || v1PreviewReleased;
}
